import { Link } from "wouter";
import { ArrowRight, CheckCircle2 } from "lucide-react";

interface ServiceCardProps {
  title: string;
  description: string;
  href: string;
  image?: string;
  icon?: React.ReactNode;
  features?: string[];
  className?: string;
}

export function ServiceCard({
  title,
  description,
  href,
  image,
  icon,
  features,
  className = ""
}: ServiceCardProps) {
  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col group ${className}`}>
      {image && (
        <div className="aspect-video w-full overflow-hidden bg-muted">
          <img
            src={image}
            alt={title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}
      
      <div className="p-6 flex flex-col flex-grow">
        <div className="flex items-start gap-4 mb-4">
          {icon && (
            <div className="p-3 rounded-full bg-primary/10 text-primary shrink-0">
              {icon}
            </div>
          )}
          <h3 className="text-xl font-serif font-bold text-card-foreground pt-1">
            {title}
          </h3>
        </div>
        
        <p className="text-muted-foreground mb-6 flex-grow">
          {description}
        </p>
        
        {features && features.length > 0 && (
          <ul className="space-y-2 mb-6">
            {features.map((feature, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-card-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}
        
        <div className="mt-auto pt-4 border-t border-border">
          <Link 
            href={href}
            className="inline-flex items-center text-primary font-bold hover:underline group-hover:gap-2 transition-all"
            aria-label={`Learn more about ${title}`}
          >
            Learn More <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      </div>
    </div>
  );
}
